import { Switch, Tooltip, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import React from 'react';

import {
  IAutomatedBidFloor,
  ResourceActive,
  ResourceActiveEnabled,
} from '~/src/dash/api/console-api';
import { LiveData } from '~/src/dash/components/live-data/live-data';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { Cols, Panel, Row, Text } from '~/src/dash/publicaUI';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { IMenuList } from '~/src/dash/types/menuList';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { AutomatedBidFloorRow } from '~/src/dash/views/bidders/Bidders/AutomatedBidFloor/automated-bid-floor-row';

interface IProps {
  RowData: IAutomatedBidFloor[];
  channels: IMenuList;
  handleChangeAutomatedBidFloor: (idx: number, field: string, value: any) => void;
  isActive: ResourceActive;
  handleChangeAutomatedBidFloorActive: () => void;
  updateSnackbar: (message: string, error: boolean) => void;
}

export const AutomatedBidFloor = ({
  RowData,
  channels,
  handleChangeAutomatedBidFloor,
  isActive,
  handleChangeAutomatedBidFloorActive,
  updateSnackbar,
}: IProps) => {
  const { user, session } = useBaseStore();

  const readAuthorization = IS_AUTHORIZED(['read_bidder'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_bidder'], user.user);

  if (!readAuthorization.isAuthorized) {
    return (
      <div>
        <ModelMissingPermission missingPermissions={readAuthorization.missingPermissions} />
      </div>
    );
  }

  return (
    <Panel
      highlighted
      largePadding
      marginLeft='16px'
      marginRight='16px'
      marginTop='16px'
      marginBottom='32px'
    >
      <Cols>
        <Text baseSize bold>
          Smart Floors
        </Text>
        <Tooltip
          title={
            'This feature is in beta. Contact your account manager if you are interested in testing this feature.'
          }
        >
          <Typography
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '10px',
              width: '40px',
              height: '20px',
              borderRadius: '3px',
              fontWeight: 500,
              fontSize: '12px',
              color: getColorTheme('betaIconText'),
              backgroundColor: getColorTheme('betaIconBackground'),
              boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.1)',
            }}
          >
            BETA
          </Typography>
        </Tooltip>
      </Cols>
      <Text extraSmall>This feature will allow users to test and optimize bid floors.</Text>
      <Cols>
        <Switch
          disabled={!writeAuthorization.isAuthorized}
          checked={isActive === ResourceActiveEnabled}
          name='Active'
          onChange={() => handleChangeAutomatedBidFloorActive()}
        />
        <Text small style={{ display: 'flex', alignItems: 'center' }}>
          {isActive === ResourceActiveEnabled ? 'Active' : 'Inactive'}
        </Text>
      </Cols>
      {isActive === ResourceActiveEnabled && (
        <>
          <Row noPadding>
            <Text baseSize bold style={{ marginTop: '16px' }}>
              Test Targeting
            </Text>
            <Text extraSmall>
              Select a channel to target, adjust floors and set test increment for the active test
              floor.
            </Text>
            <Panel
              basePadding
              marginTop='16px'
              marginBottom='16px'
              style={{
                height: '100%',
                boxShadow: 'rgb(226, 226, 226) 0px 1px 4px 1px',
                minWidth: '760px',
              }}
            >
              <Cols>
                <Text
                  bold
                  small
                  style={{
                    marginLeft: '16px',
                    width: '220px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Channel (Connected to Bidder)
                  <Tooltip
                    title={
                      "A Bid Floor value must be set in order to select channels for targeting. If using 'Honor Channel Floor', only channels whose Bid Floor value is lower than the bidder's Bid Floor value will be available for ABF targeting."
                    }
                    placement='bottom-start'
                  >
                    <InfoIcon style={{ width: '1rem', color: '#929599' }} />
                  </Tooltip>
                </Text>
                <Text
                  bold
                  small
                  style={{ paddingLeft: '24px', display: 'flex', alignItems: 'center' }}
                >
                  Base Bid Floor
                  <Tooltip
                    title={'Changing Base Floor will reset the test'}
                    placement='bottom-start'
                  >
                    <InfoIcon style={{ width: '1rem', color: '#929599' }} />
                  </Tooltip>
                </Text>
                <Text bold small style={{ paddingLeft: '24px' }}>
                  Max High Floor
                </Text>
                <Text bold small style={{ paddingLeft: '24px' }}>
                  Min Low Floor
                </Text>
                <Text bold small style={{ paddingLeft: '24px' }}>
                  Test Increment
                </Text>
              </Cols>
              {RowData.map((row, i) => (
                <AutomatedBidFloorRow
                  channels={channels}
                  rowData={row}
                  handleChangeAutomatedBidFloor={handleChangeAutomatedBidFloor}
                  index={i}
                  updateSnackbar={updateSnackbar}
                />
              ))}
            </Panel>
          </Row>
          <LiveData
            resourceIDs={RowData[0] ? RowData[0].ID : ''}
            resourceType={'AutomatedBidFloor'}
            timezone={session.publisher.Timezone}
            publisher={session.publisher}
            userInfo={user.user}
          />
        </>
      )}
    </Panel>
  );
};
